import { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavigationModule } from '../components/layout/types';
import { navigationModules } from '../components/layout/navigationModules';
import { getModuleConfig, getPageConfig, ModuleConfigView, PageConfigView } from '../api/Navigation/moduleConfig';
import { useComplianceCountry } from './useComplianceCountry';
import { useSignalRConnection } from './useSignalRConnection';

interface RootState {
  user: {
    role: string;
    isLoggedIn: boolean;
    isManager: boolean;
    userId: string;
  };
}

/**
 * Filters navigation modules based on:
 * 1. User role (local filter)
 * 2. Company module configuration (API-based, Operator controlled)
 *
 * Filtering rules:
 * - If module has no roles array, it's visible to all
 * - If module has roles array, user's role must be in it
 * - Same rules apply to pages within modules
 * - Modules with no visible pages are excluded
 * - Company config further restricts which modules are visible
 * - Empty company config means all modules enabled (backwards compatible)
 *
 * @returns Filtered array of NavigationModule based on user role and company config
 */
export function useFilteredNavigation(): NavigationModule[] {
  const role = useSelector((state: RootState) => state.user.role);
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const isManager = useSelector((state: RootState) => state.user.isManager);
  const userId = useSelector((state: RootState) => state.user.userId);
  const complianceCountry = useComplianceCountry();
  // Effective shift-swap enablement = per-user override (Enabled/Disabled) else the company default.
  // Mirrors ShiftSwapService.ResolveShiftSwapEnabled on the server (which stays the security boundary).
  const shiftSwapConfig = useSelector((state: { user?: { userRules?: { shiftSwapConfig?: string } } }) => state.user?.userRules?.shiftSwapConfig);
  const companyShiftSwapEnabled = useSelector((state: { user?: { company?: { shiftSwapEnabled?: boolean } } }) => state.user?.company?.shiftSwapEnabled);
  const companyId = useSelector((state: { user?: { company?: { id?: number } } }) => state.user?.company?.id);
  const { subscribe, subscribeToCompanyGroup } = useSignalRConnection();
  const [companyConfig, setCompanyConfig] = useState<ModuleConfigView[]>([]);
  const [pageConfig, setPageConfig] = useState<PageConfigView[]>([]);
  const [configLoaded, setConfigLoaded] = useState(false);
  // Bumped by the ModuleConfigUpdated SignalR event to force a config re-fetch
  // without a logout/login (backlog P3: real-time module/page config sync).
  const [refetchNonce, setRefetchNonce] = useState(0);

  // Fetch company module config and page config on mount, when login state
  // changes, and whenever the real-time refetch nonce is bumped.
  useEffect(() => {
    if (!isLoggedIn) {
      setCompanyConfig([]);
      setPageConfig([]);
      setConfigLoaded(true);
      return;
    }

    let cancelled = false;

    Promise.all([getModuleConfig(), getPageConfig()])
      .then(([moduleConf, pageConf]) => {
        if (!cancelled) {
          setCompanyConfig(moduleConf);
          setPageConfig(pageConf);
          setConfigLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConfigLoaded(true); // Continue with empty config on error
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, refetchNonce]);

  // Real-time module/page config sync: when the Operator changes this company's
  // module/page enablement, the backend broadcasts ModuleConfigUpdated to the
  // company group. Re-fetch so the sidebar updates live (no logout/login). The
  // company group is idempotently shared with useCompanyRulesSignalR.
  useEffect(() => {
    if (!isLoggedIn || !companyId) return;

    subscribeToCompanyGroup(companyId).catch(err => {
      console.error('Failed to subscribe to company group:', err);
    });

    const unsubscribe = subscribe('ModuleConfigUpdated', () => {
      setRefetchNonce(n => n + 1);
    });

    return () => {
      unsubscribe();
    };
  }, [isLoggedIn, companyId, subscribe, subscribeToCompanyGroup]);

  return useMemo(() => {
    if (!isLoggedIn) return [];

    // Build effective roles: the user's role + "Manager" if isManager flag is set
    const effectiveRoles = new Set([role]);
    if (isManager) effectiveRoles.add('Manager');

    const hasRole = (allowedRoles: string[]) =>
      allowedRoles.some(r => effectiveRoles.has(r));

    // A page/sub-module with NO `roles` array INHERITS its parent module's gate. That is deliberate
    // (most modules want it), but it means a role-less leaf under a permissive module gate is offered
    // to every role in that gate — and if the leaf's App.tsx route is narrower, the menu entry
    // dead-ends in a 403. Leaves under such modules (notably Settings, whose module gate includes
    // Operator) must declare explicit `roles` mirroring their route guard. See navigationModules.ts.
    const isPageVisibleByRole = (rolesArr?: string[]) => !rolesArr || hasRole(rolesArr);

    // First: filter by role (modules, their direct pages, sub-modules, and sub-module pages)
    let filtered = navigationModules
      .filter(module => isPageVisibleByRole(module.roles))
      // Hide every module that sits under the "Admin Section" separator from
      // users whose only elevated role is Admin. Admin nav should mirror a
      // regular HR/Clocking user — minus the platform-config surface that the
      // Admin Section gathers.
      .filter(module => !(role === 'Admin' && module.isAdmin))
      .map(module => ({
        ...module,
        pages: module.pages.filter(page => isPageVisibleByRole(page.roles)),
        subModules: module.subModules
          ?.filter(sub => isPageVisibleByRole(sub.roles))
          .map(sub => ({
            ...sub,
            pages: sub.pages.filter(page => isPageVisibleByRole(page.roles)),
          }))
          .filter(sub => sub.pages.length > 0),
      }))
      // Remove modules with no visible pages AND no visible sub-modules
      .filter(module => module.pages.length > 0 || (module.subModules?.length ?? 0) > 0);

    // Second: filter by company config (if config exists)
    // Empty companyConfig means all modules enabled (default behavior)
    if (companyConfig.length > 0) {
      const enabledModuleIds = new Set(
        companyConfig.filter(c => c.isEnabled).map(c => c.moduleId)
      );

      // Build set of configured module IDs
      const configuredModuleIds = new Set(companyConfig.map(c => c.moduleId));

      // If config exists, only show modules that are:
      // - Explicitly enabled in config, OR
      // - Not mentioned in config at all (backwards compatibility)
      filtered = filtered.filter(module => {
        // If no config for this module, show it (default enabled)
        if (!configuredModuleIds.has(module.id)) return true;
        // Otherwise, check if enabled
        return enabledModuleIds.has(module.id);
      });

      // Apply display order from config. For modules not yet in the company
      // config (e.g. newly added modules), fall back to their position in
      // navigationModules so they slot in at their source-defined location
      // instead of being dumped after the Admin section.
      const orderMap = new Map(
        companyConfig.map(c => [c.moduleId, c.displayOrder ?? 999])
      );
      const sourceOrder = new Map(
        navigationModules.map((m, i) => [m.id, i])
      );
      filtered.sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? sourceOrder.get(a.id) ?? 999;
        const orderB = orderMap.get(b.id) ?? sourceOrder.get(b.id) ?? 999;
        return orderA - orderB;
      });
    }

    // Third: filter by page config (if page config exists)
    // Empty pageConfig means all pages visible (backwards compatible)
    if (pageConfig.length > 0) {
      const disabledPages = new Set(
        pageConfig.filter(p => !p.isEnabled).map(p => p.pagePath)
      );

      if (disabledPages.size > 0) {
        filtered = filtered.map(module => ({
          ...module,
          pages: module.pages.filter(page => !disabledPages.has(page.path)),
          subModules: module.subModules
            ?.map(sub => ({
              ...sub,
              pages: sub.pages.filter(page => !disabledPages.has(page.path)),
            }))
            .filter(sub => sub.pages.length > 0),
        })).filter(module =>
          module.pages.length > 0 || (module.subModules?.length ?? 0) > 0
        );
      }
    }

    // Resolve dynamic path tokens (e.g., :userId) — applies to direct + sub-module pages
    if (userId) {
      const resolvePath = (page: { path: string }) =>
        page.path.includes(':userId')
          ? { ...page, path: page.path.replace(':userId', userId) }
          : page;
      filtered = filtered.map(module => ({
        ...module,
        pages: module.pages.map(resolvePath),
        subModules: module.subModules?.map(sub => ({
          ...sub,
          pages: sub.pages.map(resolvePath),
        })),
      }));
    }

    // Filter compliance pages by country:
    // - Compliance Center, WPS Center, and Compliance Settings are shared by
    //   UAE and KSA (both have WPS-like mandates and country-specific identifiers).
    // - EOS Center embeds UAE-specific gratuity rules and stays UAE-only.
    // Per-profile EOS and EOS settings are generic and stay visible everywhere.
    const SHARED_COMPLIANCE_PATHS = new Set([
      '/payroll/compliance-center',
      '/settings/payroll/compliance',
      '/payroll/wps-center',
      '/payroll/pension',
    ]);
    const UAE_ONLY_PATHS = new Set([
      '/eos-center',
    ]);
    const isUaeOrKsa = complianceCountry === 'UAE' || complianceCountry === 'KSA';
    const isOperator = role === 'Operator';
    const isCompliancePageVisible = (page: { path: string }) => {
      if (page.path === '/settings/payroll/compliance' && isOperator) return true;
      // Hide shared compliance pages from tenants outside UAE/KSA
      if (!isUaeOrKsa && SHARED_COMPLIANCE_PATHS.has(page.path)) return false;
      // Hide UAE-only pages from any non-UAE tenant (including KSA)
      if (complianceCountry !== 'UAE' && UAE_ONLY_PATHS.has(page.path)) return false;
      return true;
    };
    // Apply to direct pages AND sub-module pages, and keep any module that
    // still has content in either bucket. (The Settings module is a tree of
    // sub-modules with empty top-level `pages`, so checking only `pages.length`
    // here would drop it entirely for HR/Admin.)
    filtered = filtered.map(module => ({
      ...module,
      pages: module.pages.filter(isCompliancePageVisible),
      subModules: module.subModules
        ?.map(sub => ({
          ...sub,
          pages: sub.pages.filter(isCompliancePageVisible),
        }))
        .filter(sub => sub.pages.length > 0),
    })).filter(module =>
      module.pages.length > 0 || (module.subModules?.length ?? 0) > 0,
    );

    // Hide the Shift Swaps page unless the feature is enabled for this user. The parent "attendance"
    // module keeps its other pages, so this only drops the single item (and prunes an empty module).
    const shiftSwapEnabled =
      shiftSwapConfig === 'Enabled' ? true :
      shiftSwapConfig === 'Disabled' ? false :
      !!companyShiftSwapEnabled;
    if (!shiftSwapEnabled) {
      const notShiftSwap = (page: { path: string }) => page.path !== '/shift-swaps';
      filtered = filtered.map(module => ({
        ...module,
        pages: module.pages.filter(notShiftSwap),
        subModules: module.subModules
          ?.map(sub => ({ ...sub, pages: sub.pages.filter(notShiftSwap) }))
          .filter(sub => sub.pages.length > 0),
      })).filter(module =>
        module.pages.length > 0 || (module.subModules?.length ?? 0) > 0,
      );
    }

    return filtered;
  }, [role, isLoggedIn, isManager, userId, companyConfig, pageConfig, complianceCountry, shiftSwapConfig, companyShiftSwapEnabled]);
}

export default useFilteredNavigation;
